import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './OperatorInvoices.css'; // Shared premium design system
import { formatCurrency, getCurrencyCode } from '../utils/formatters';

const API = '/api/bank-accounts';

/* ── Inline SVG Icons ── */
const Icon = ({ d, size = 16, strokeWidth = 1.75 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24"
        fill="none" stroke="currentColor"
        strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"
        style={{ display: 'inline-block', verticalAlign: 'middle' }}>
        {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
    </svg>
);

const icons = {
    plus:    ['M12 5v14', 'M5 12h14'],
    edit:    ['M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7', 'M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z'],
    trash:   ['M3 6h18', 'M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2'],
    x:       ['M18 6 6 18', 'M6 6l12 12'],
    bank:    ['M3 21h18', 'M3 10h18', 'M5 6l7-3 7 3', 'M4 10v11', 'M20 10v11', 'M8 14v3', 'M12 14v3', 'M16 14v3'],
    wallet:  ['M20 12V8H6a2 2 0 0 1-2-2V5', 'M4 19v-2a2 2 0 0 1 2-2h14v3a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z'],
    check:   ['M22 11.08V12a10 10 0 1 1-5.93-9.14', 'M22 4 12 14.01l-3-3'],
    grid:    ['M3 3h7v7H3z', 'M14 3h7v7h-7z', 'M14 14h7v7h-7z', 'M3 14h7v7H3z'],
};

const EMPTY_FORM = {
    accountName: '', bankName: '', accountNumber: '', iban: '',
    accountType: 'Current', currency: 'AED',
    openingBalance: 0, currentBalance: 0,
    status: 'Active', branch: '', swiftCode: '',
    contactPerson: '', contactPhone: '', notes: '',
};

export default function BankAccounts({ user: propUser, operatorDetails }) {
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);

    const user = propUser || JSON.parse(localStorage.getItem('ops-user'));
    const operatorId = user?.operatorId || (user?.userType === 'operator' ? user._id : null);
    const country = operatorDetails?.country;

    useEffect(() => { if (operatorId) fetchAccounts(); }, [operatorId]);

    const fetchAccounts = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/${operatorId}`);
            setAccounts(res.data);
        } catch (err) { console.error('Fetch bank accounts error:', err); }
        finally { setLoading(false); }
    };

    const openModal = (item = null) => {
        setEditItem(item);
        setForm(item ? { ...item } : { ...EMPTY_FORM, currency: getCurrencyCode(operatorDetails?.country) });
        setModalOpen(true);
    };

    const closeModal = () => { setModalOpen(false); setEditItem(null); };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            if (editItem) {
                await axios.put(`${API}/${editItem._id}`, form);
            } else {
                await axios.post(API, { ...form, operatorId });
            }
            closeModal();
            fetchAccounts();
        } catch (err) {
            alert('Failed to save bank account');
            console.error(err);
        } finally { setSaving(false); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this bank account?')) return;
        try {
            await axios.delete(`${API}/${id}`);
            fetchAccounts();
        } catch (err) { console.error(err); }
    };

    /* ── KPI Calculations ── */
    const activeAccounts = accounts.filter(a => a.status === 'Active');
    const totalBalance = activeAccounts.reduce((s, a) => s + (a.currentBalance || 0), 0);

    return (
        <div className="operator-page">
            {/* Breadcrumb */}
            <div className="op-breadcrumb">
                <span className="home-icon">🏠</span>
                <span className="sep">›</span>
                <span>Finance</span>
                <span className="sep">›</span>
                <span className="current">Bank Accounts</span>
            </div>

            {/* Header */}
            <div className="op-header">
                <div className="op-header-left">
                    <h1 className="op-title">Bank Accounts</h1>
                    <p className="op-subtitle">Manage company bank accounts</p>
                </div>
                <div className="op-header-actions">
                    <button className="op-btn op-btn-solid-primary" onClick={() => openModal()}>
                        <Icon d={icons.plus} size={15} />
                        Add Bank Account
                    </button>
                </div>
            </div>

            {/* KPI Cards */}
            <div className="op-kpi-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', gap: 18, marginBottom: 26 }}>
                <div style={{ background: 'white', borderRadius: 18, padding: '22px 24px', border: '1px solid #e2e8f0', borderTop: '4px solid #2563eb', boxShadow: '0 4px 12px rgba(0,0,0,0.04)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                        <div style={{ fontSize: 11, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Balance</div>
                        <div style={{ width: 38, height: 38, borderRadius: 10, background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Icon d={icons.wallet} size={18} strokeWidth={2} />
                        </div>
                    </div>
                    <div style={{ fontSize: 28, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', lineHeight: 1 }}>{formatCurrency(totalBalance, country)}</div>
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 8, fontWeight: 500 }}>Active accounts only</div>
                </div>

                <div style={{ background: 'white', borderRadius: 18, padding: '22px 24px', border: '1px solid #e2e8f0', borderTop: '4px solid #1d4ed8', boxShadow: '0 4px 12px rgba(0,0,0,0.04)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                        <div style={{ fontSize: 11, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Active Accounts</div>
                        <div style={{ width: 38, height: 38, borderRadius: 10, background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Icon d={icons.check} size={18} strokeWidth={2} />
                        </div>
                    </div>
                    <div style={{ fontSize: 28, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', lineHeight: 1 }}>{activeAccounts.length}</div>
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 8, fontWeight: 500 }}>Operational accounts</div>
                </div>

                <div style={{ background: 'white', borderRadius: 18, padding: '22px 24px', border: '1px solid #e2e8f0', borderTop: '4px solid #0284c7', boxShadow: '0 4px 12px rgba(0,0,0,0.04)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                        <div style={{ fontSize: 11, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Accounts</div>
                        <div style={{ width: 38, height: 38, borderRadius: 10, background: '#f0f9ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Icon d={icons.grid} size={18} strokeWidth={2} />
                        </div>
                    </div>
                    <div style={{ fontSize: 28, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', lineHeight: 1 }}>{accounts.length}</div>
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 8, fontWeight: 500 }}>All bank accounts</div>
                </div>
            </div>

            {/* Accounts Table */}
            <div className="op-records-card">
                <div className="op-records-header">
                    <h2 className="op-records-title">Bank Accounts ({accounts.length})</h2>
                </div>
                <div className="op-table-wrapper">
                    <table className="op-records-table">
                        <thead>
                            <tr>
                                <th>Account Name</th>
                                <th>Bank Name</th>
                                <th>Account Number</th>
                                <th>IBAN</th>
                                <th>Type</th>
                                <th>Current Balance</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="8" style={{ textAlign: 'center', padding: '50px', color: '#94a3b8' }}>Loading accounts…</td></tr>
                            ) : accounts.length === 0 ? (
                                <tr><td colSpan="8" style={{ textAlign: 'center', padding: '50px', color: '#94a3b8' }}>
                                    No bank accounts yet — click "Add Bank Account" to start
                                </td></tr>
                            ) : accounts.map(acc => (
                                <tr key={acc._id}>
                                    <td style={{ fontWeight: 700, color: '#1e293b' }}>{acc.accountName}</td>
                                    <td style={{ fontWeight: 500 }}>{acc.bankName}</td>
                                    <td style={{ fontFamily: 'monospace', fontSize: 13, letterSpacing: '0.5px' }}>{acc.accountNumber}</td>
                                    <td style={{ fontFamily: 'monospace', fontSize: 12, color: '#64748b' }}>{acc.iban || '—'}</td>
                                    <td>
                                        <span style={{
                                            display: 'inline-block', padding: '3px 10px',
                                            borderRadius: 8, fontSize: 11, fontWeight: 700,
                                            background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe'
                                        }}>{acc.accountType}</span>
                                    </td>
                                    <td style={{ fontWeight: 800, color: '#0f172a' }}>
                                        {formatCurrency(acc.currentBalance, country)}
                                    </td>
                                    <td>
                                        <span style={{
                                            display: 'inline-flex', alignItems: 'center', gap: 4,
                                            padding: '4px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700,
                                            background: acc.status === 'Active' ? '#dcfce7' : acc.status === 'Inactive' ? '#fef3c7' : '#fee2e2',
                                            color: acc.status === 'Active' ? '#166534' : acc.status === 'Inactive' ? '#92400e' : '#991b1b',
                                            border: `1px solid ${acc.status === 'Active' ? '#86efac' : acc.status === 'Inactive' ? '#fde68a' : '#fca5a5'}`
                                        }}>
                                            {acc.status === 'Active' && '● '}{acc.status}
                                        </span>
                                    </td>
                                    <td className="inv-actions">
                                        <button title="Edit" className="op-action-btn" onClick={() => openModal(acc)}>
                                            <Icon d={icons.edit} size={13} />
                                        </button>
                                        <button title="Delete" className="op-action-btn btn-delete" onClick={() => handleDelete(acc._id)}>
                                            <Icon d={icons.trash} size={13} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Add / Edit Modal */}
            {modalOpen && (
                <div className="invoice-modal-overlay" onClick={closeModal}>
                    <div className="invoice-modal-content" style={{ width: 680 }} onClick={e => e.stopPropagation()}>
                        <div className="invoice-modal-header">
                            <h2>{editItem ? 'Edit Bank Account' : 'Add Bank Account'}</h2>
                            <button className="close-btn" onClick={closeModal}><Icon d={icons.x} size={18} /></button>
                        </div>
                        <form onSubmit={handleSave}>
                            <div className="invoice-modal-body" style={{ padding: 28 }}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>

                                    <div className="form-group">
                                        <label>Account Name *</label>
                                        <input required type="text" placeholder="e.g., Commercial Bank - Current"
                                            value={form.accountName} onChange={e => setForm({ ...form, accountName: e.target.value })} />
                                    </div>

                                    <div className="form-group">
                                        <label>Bank Name *</label>
                                        <input required type="text" placeholder="e.g., Emirates NBD"
                                            value={form.bankName} onChange={e => setForm({ ...form, bankName: e.target.value })} />
                                    </div>

                                    <div className="form-group">
                                        <label>Account Number *</label>
                                        <input required type="text" placeholder="e.g., 1234567890"
                                            value={form.accountNumber} onChange={e => setForm({ ...form, accountNumber: e.target.value })} />
                                    </div>

                                    <div className="form-group">
                                        <label>IBAN</label>
                                        <input type="text" placeholder="e.g., AE070331234567890123456"
                                            value={form.iban} onChange={e => setForm({ ...form, iban: e.target.value })} />
                                    </div>

                                    <div className="form-group">
                                        <label>Account Type</label>
                                        <select value={form.accountType} onChange={e => setForm({ ...form, accountType: e.target.value })}>
                                            {['Current', 'Savings', 'Fixed Deposit', 'Overdraft', 'Other'].map(t => (
                                                <option key={t} value={t}>{t}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label>Currency</label>
                                        <input type="text" placeholder={getCurrencyCode(operatorDetails?.country)}
                                            value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })} />
                                    </div>

                                    <div className="form-group">
                                        <label>Opening Balance</label>
                                        <input type="number" step="0.01" value={form.openingBalance}
                                            onChange={e => setForm({ ...form, openingBalance: e.target.value })} />
                                    </div>

                                    <div className="form-group">
                                        <label>Current Balance</label>
                                        <input type="number" step="0.01" value={form.currentBalance}
                                            onChange={e => setForm({ ...form, currentBalance: e.target.value })} />
                                    </div>

                                    <div className="form-group">
                                        <label>Status</label>
                                        <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                                            <option value="Active">Active</option>
                                            <option value="Inactive">Inactive</option>
                                            <option value="Closed">Closed</option>
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label>Branch</label>
                                        <input type="text" placeholder="e.g., Dubai Main Branch"
                                            value={form.branch} onChange={e => setForm({ ...form, branch: e.target.value })} />
                                    </div>

                                    <div className="form-group">
                                        <label>Swift / BIC Code</label>
                                        <input type="text" placeholder="e.g., EBILAEAD"
                                            value={form.swiftCode} onChange={e => setForm({ ...form, swiftCode: e.target.value })} />
                                    </div>

                                    <div className="form-group">
                                        <label>Contact Person</label>
                                        <input type="text" placeholder="e.g., Ahmed Al Ali"
                                            value={form.contactPerson} onChange={e => setForm({ ...form, contactPerson: e.target.value })} />
                                    </div>

                                    <div className="form-group">
                                        <label>Contact Phone</label>
                                        <input type="text" placeholder="e.g., +971 4 1234567"
                                            value={form.contactPhone} onChange={e => setForm({ ...form, contactPhone: e.target.value })} />
                                    </div>

                                    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                                        <label>Notes</label>
                                        <textarea rows={3} placeholder="Additional notes about this account..."
                                            value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                                            style={{ width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: 12, fontFamily: 'inherit', fontSize: 14, background: '#f8fafc', resize: 'vertical', outline: 'none' }} />
                                    </div>
                                </div>
                            </div>
                            <div className="invoice-modal-footer">
                                <button type="button" className="btn-cancel" onClick={closeModal}>Cancel</button>
                                <button type="submit" className="btn-save" disabled={saving} style={{ background: 'linear-gradient(135deg, #16539C, #0D3363)' }}>
                                    {saving ? 'Saving…' : editItem ? 'Update Account' : 'Add Account'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
